'use client';
import { useEffect, useCallback, useRef } from 'react';
import { useRouter } from 'next/navigation';
import moment from 'moment';
import { useUser } from '@/app/hooks/useUser';
import { clearUserCookies, getAccessToken, setCookieUser, setExpiredToken } from '@/app/helpers/cookiesMethod';
import { useAuthenticationPopUp } from '@/app/contexts/authentication-popup-context';
import { setAppPlatform, isAppRatingRequested, setAppRatingRequested, isOnboardingCompleted } from '@/app/helpers/appStorageMethod';
import { NotificationService } from '@/app/service/notifications';

/**
 * GlobalAppBridgeHandler
 * 
 * This component listens for 'joysamData' events from the Native App (WebView)
 * and synchronizes authentication tokens, user data, and device info globally.
 * 
 * By placing this in the Root Layout, we ensure that token synchronization
 * happens regardless of which page the user is currently on (Home, Community, etc.)
 */
export function GlobalAppBridgeHandler() {
    const { updateDataFromCookiesForProvider, auth, user } = useUser();
    const { confirmPopUp } = useAuthenticationPopUp();
    const router = useRouter();

    const hasRequestedReviewThisSession = useRef<boolean>(false);

    // Check eligibility for rating pop-up
    const checkAndTriggerReview = useCallback(async () => {
        try {
            const isLoggedIn = !!auth?.id || !!user?.id;

            // 1. Chỉ gọi tối đa 1 lần trong phiên chạy app hiện tại
            if (hasRequestedReviewThisSession.current) return;

            // 2. User phải ở trạng thái Đăng nhập
            if (!isLoggedIn) return;

            // 3. Phải hoàn thành onboarding mới cho phép hiện review
            const onboardingCompleted = await isOnboardingCompleted();
            if (!onboardingCompleted) return;

            // 4. CASE 3: Nếu vừa mới đăng ký tài khoản ở phiên này -> Không hiện review
            if (typeof window !== 'undefined' && (window as any).__just_registered_this_session === true) {
                //alert('Review debug: __just_registered_this_session is true (first usage after registration, won\'t show review)');
                return;
            }

            // 5. Kiểm tra xem user này đã bao giờ yêu cầu review chưa (lưu vĩnh viễn trong local/app storage)
            const alreadyRequested = await isAppRatingRequested();
            if (alreadyRequested) {
                //alert('Review debug: Already requested review in the past (flag is true)');
                return;
            }

            //alert(`Review debug: Passing all checks. appBridge exists: ${!!window.appBridge}. requestReview type: ${typeof (window.appBridge as any)?.requestReview}`);

            // 6. Gọi lên Native App để hiển thị pop-up review
            if (
                typeof window !== 'undefined' &&
                window.appBridge &&
                typeof (window.appBridge as any).requestReview === 'function'
            ) {
                hasRequestedReviewThisSession.current = true;
                // Lưu flag đã yêu cầu review vào bộ nhớ vĩnh viễn trước
                await setAppRatingRequested();
                // Gọi API native review
                //alert('Review debug: Triggering appBridge.requestReview()...');
                await (window.appBridge as any).requestReview();
                //alert('Review debug: Called appBridge.requestReview() successfully!');
            } else {
                //alert('Review debug: appBridge or requestReview function NOT found on window object.');
            }
        } catch (error) {
            //alert(`Review debug error: ${error instanceof Error ? error.message : String(error)}`);
            console.error('[GlobalAppBridge] Error in checkAndTriggerReview:', error);
        }
    }, [auth?.id, user?.id]);

    useEffect(() => {
        checkAndTriggerReview();
    }, [checkAndTriggerReview]);

    useEffect(() => {
        if (typeof window === 'undefined') return;

        const handleOnboardingCompleted = () => {
            checkAndTriggerReview();
        };

        window.addEventListener('onboardingCompleted', handleOnboardingCompleted);
        return () => {
            window.removeEventListener('onboardingCompleted', handleOnboardingCompleted);
        };
    }, [checkAndTriggerReview]);

    const updateFCM = useCallback(async (platform: string, fcm: string) => {
        try {
            if (!fcm) return;
            await NotificationService.updateNotifications({ platform, fcm });
        } catch (error) {
            console.error('[GlobalAppBridge] FCM update failed:', error);
        }
    }, []);

    useEffect(() => {
        const handleMessage = async (event: any) => {
            try {
                const detail = event.detail;
                if (!detail) return;

                const data = typeof detail === 'string' ? JSON.parse(detail) : detail;
                const isAllowNotifications = data.IS_ALLOW_NOTIFICATIONS_WEBVIEW;
                const fcmToken = data.FCM_TOKEN;
                const platform = data.PLATFORM;
                const userId = data?.user?.id;
                const getIsAllowNotifications = localStorage.getItem('isAllowNotifications');

                if (platform) {
                    await setAppPlatform(platform);
                }

                if (userId) {
                    try {
                        await window.appBridge?.storageSetItem('_joysamUserId', String(userId));
                    } catch (error) {
                        console.error('[GlobalAppBridge] Error storing userId:', error);
                    }
                }

                const expiredToken = data.expired_at;
                auth?.updateRegistrationStatus(data?.is_registered ?? false);
                setExpiredToken(expiredToken);

                if (data.is_registered && data.access_token) {
                    const userObject = {
                        id: data?.user?.id,
                        nickname: data?.user?.nickname || '',
                        email: data?.user?.email || undefined,
                        contacts: data?.user?.contacts ?? [],
                        isStaff: data?.user?.is_staff ?? null,
                        isInfluencer: data?.user?.is_influencer ?? null,
                        isExpert: data?.user?.is_expert ?? null,
                        avatarUrl: data?.user?.avatar ?? null,
                        isGoogleLoggedIn: data?.user?.is_google_logged_in ?? false,
                        isAppleLoggedIn: data?.user?.is_apple_logged_in ?? false,
                        isKakaoLoggedIn: data?.user?.is_kakao_logged_in ?? false,
                        isNaverLoggedIn: data?.user?.is_naver_logged_in ?? false,
                        phoneNumber: data?.user?.phone_number ?? null,
                        updatedAt: data?.user?.updated_at ? moment(data?.user.updated_at) : moment(),
                        createdAt: data?.user?.created_at ? moment(data?.user.created_at) : moment(),
                        level: data?.user?.level,
                    };

                    const currentToken = getAccessToken();

                    // Only sync if token actually changed or is new
                    if (currentToken !== data.access_token) {
                        if (currentToken) await clearUserCookies();

                        // Step 1: Save token to cookie
                        await setCookieUser({
                            user: userObject,
                            accessToken: data?.access_token,
                            refreshToken: data?.refresh_token || '',
                            fireBaseToken: data?.access_token,
                        });

                        // Step 2: Signal UserProvider to update its internal state
                        // This will trigger re-renders and re-fetches in pages that depend on user.id
                        if (updateDataFromCookiesForProvider) {
                            await updateDataFromCookiesForProvider();
                        }

                        // Step 3: Close any open authentication popups
                        // This handles race conditions where a 401 might have triggered a popup
                        // just before we received the new token.
                        if (confirmPopUp) {
                            confirmPopUp.close();
                        }
                    }

                    // Store notification settings
                    localStorage.setItem('isAllowNotifications', String(isAllowNotifications ?? false));
                    localStorage.setItem('fcmToken', String(fcmToken));
                    await updateFCM(platform, fcmToken);

                    if (isAllowNotifications && !getIsAllowNotifications) {
                        await NotificationService.enableNotifications({ notificationType: 'is_system', isEnable: 1 });
                        await NotificationService.enableNotifications({ notificationType: 'is_event', isEnable: 1 });
                    }
                    if (!isAllowNotifications && !getIsAllowNotifications) {
                        await NotificationService.enableNotifications({ notificationType: 'is_system', isEnable: 0 });
                    }
                }
            } catch (error) {
                console.error('[GlobalAppBridge] Critical error handling joysamData:', error);
            }
        };
        if (window?.ReactNativeWebView) {
            window?.ReactNativeWebView?.postMessage('listenerReady');
            window.addEventListener('joysamData', handleMessage);
        }

        return () => {
            window.removeEventListener('joysamData', handleMessage);
        };
    }, [auth, updateDataFromCookiesForProvider, updateFCM]);

    return null;
}
